import React, { useState } from "react";
import axios from "axios";
import { trackingContent } from "../mock";
import TrackingIllustration from "./TrackingIllustration";
import useReveal from "../hooks/useReveal";
import { Search, PackageCheck, MapPin, CheckCircle2, Circle, Loader2, Calendar, CalendarCheck, Boxes, User, ArrowRight, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDT = (ts) => {
  if (!ts) return "";
  const d = new Date(ts);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const renderRich = (parts) =>
  parts.map((p, i) =>
    p.bold ? (
      <strong key={i} className="font-semibold text-white">
        {p.text}
      </strong>
    ) : (
      <span key={i}>{p.text}</span>
    )
  );

const TrackingSection = () => {
  const ref = useReveal();
  const [code, setCode] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSearch = async () => {
    const clean = code.trim();
    setResult(null);
    setError("");
    if (!clean) {
      setError("Inserisci un codice spedizione valido.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/track`, { code: `SF-${clean}` });
      setResult(data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(detail || "Errore durante la ricerca. Riprova pi\u00f9 tardi.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="bg-white py-6 md:py-10">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div
          ref={ref}
          className="reveal relative rounded-[26px] bg-[#12121C] overflow-hidden shadow-2xl"
        >
          <div className="grid md:grid-cols-2 gap-8 p-8 md:p-14 items-center">
            {/* Left: form + text */}
            <div>
              <h2 className="text-white font-bold text-3xl md:text-4xl tracking-tight">
                {trackingContent.title}
              </h2>

              <label className="block text-white font-semibold text-sm mt-8 mb-2">
                {trackingContent.label}
              </label>
              <div className="flex items-stretch bg-white rounded-md overflow-hidden shadow-lg max-w-md">
                <span className="flex items-center px-4 font-bold text-[#12121C] bg-gray-100 border-r border-gray-200">
                  {trackingContent.prefix}
                </span>
                <input
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\s/g, ""))}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  placeholder={trackingContent.placeholder}
                  className="flex-1 px-4 py-3.5 text-[#12121C] outline-none placeholder:text-gray-400"
                />
              </div>
              <p className="text-gray-300 text-xs font-semibold mt-2">
                {trackingContent.example}
              </p>

              <button
                onClick={handleSearch}
                disabled={loading}
                className="mt-6 inline-flex items-center gap-2 bg-[#3a3a45] hover:bg-[#E23028] disabled:opacity-70 disabled:cursor-not-allowed text-white font-medium px-7 py-3 rounded-md transition-colors duration-300"
              >
                {loading ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <Search size={18} />
                )}{" "}
                {loading ? "Ricerca in corso..." : trackingContent.button}
              </button>

              {/* Result / error */}
              {error && (
                <div className="mt-5 bg-[#E23028]/15 border border-[#E23028]/40 text-[#ffb3ae] text-sm rounded-md px-4 py-3 max-w-md">
                  {error}
                </div>
              )}
              {result && (
                <div className="mt-5 bg-white/5 border border-white/10 rounded-lg p-4 max-w-md">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 text-[#00C2FF] font-semibold">
                      <PackageCheck size={18} /> Stato: {result.status}
                    </div>
                    <span className="text-xs font-mono text-gray-300">{result.code}</span>
                  </div>

                  {/* Dettagli spedizione */}
                  <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                    {(result.origin || result.destination) && (
                      <div className="col-span-2 flex items-center gap-2 text-gray-200">
                        <MapPin size={14} className="text-[#00C2FF] shrink-0" />
                        <span>{result.origin || "?"}</span>
                        <ArrowRight size={13} className="text-gray-500" />
                        <span>{result.destination || "?"}</span>
                      </div>
                    )}
                    {result.sender && (
                      <div>
                        <p className="text-[11px] uppercase tracking-wide text-gray-400">Mittente</p>
                        <p className="text-white flex items-center gap-1"><User size={13} /> {result.sender}</p>
                      </div>
                    )}
                    {result.recipient && (
                      <div>
                        <p className="text-[11px] uppercase tracking-wide text-gray-400">Destinatario</p>
                        <p className="text-white flex items-center gap-1"><User size={13} /> {result.recipient}</p>
                      </div>
                    )}
                    {result.departure_date && (
                      <div>
                        <p className="text-[11px] uppercase tracking-wide text-gray-400">Data partenza</p>
                        <p className="text-white flex items-center gap-1"><Calendar size={13} /> {result.departure_date}</p>
                      </div>
                    )}
                    {result.expected_delivery && (
                      <div>
                        <p className="text-[11px] uppercase tracking-wide text-gray-400">Consegna prevista</p>
                        <p className="text-white flex items-center gap-1"><CalendarCheck size={13} /> {result.expected_delivery}</p>
                      </div>
                    )}
                  </div>

                  {/* Colli */}
                  {result.packages && result.packages.length > 0 && (
                    <div className="mt-4 border-t border-white/10 pt-3">
                      <p className="text-[11px] uppercase tracking-wide text-gray-400 flex items-center gap-1">
                        <Boxes size={13} /> Colli ({result.packages.length})
                      </p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {result.packages.map((p, i) => (
                          <span key={i} className="text-xs bg-white/10 text-gray-100 rounded-md px-2.5 py-1">
                            Collo {i + 1}: {p.weight || "n/d"}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Timeline stati */}
                  {result.steps && result.steps.length > 0 && (
                    <ul className="mt-4 border-t border-white/10 pt-4 space-y-3">
                      {result.steps.map((s, i) => (
                        <li key={i} className="flex items-start gap-3">
                          {s.done ? (
                            <CheckCircle2 size={18} className="text-[#3ecf6e] mt-0.5" />
                          ) : (
                            <Circle size={18} className="text-gray-500 mt-0.5" />
                          )}
                          <div className="flex-1">
                            <div className="flex items-center justify-between gap-2">
                              <p className={`text-sm font-medium ${s.done ? "text-white" : "text-gray-400"}`}>
                                {s.label}
                              </p>
                              {s.done && s.timestamp && (
                                <span className="text-[11px] text-gray-400 whitespace-nowrap flex items-center gap-1">
                                  <Clock size={11} /> {formatDT(s.timestamp)}
                                </span>
                              )}
                            </div>
                            {s.place && (
                              <p className="text-xs text-gray-400 flex items-center gap-1">
                                <MapPin size={12} /> {s.place}
                              </p>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              <div className="mt-8 space-y-4 text-[#b9bac6] text-[15px] leading-relaxed">
                {trackingContent.paragraphs.map((para, i) => (
                  <p key={i}>{renderRich(para)}</p>
                ))}
              </div>
            </div>

            {/* Right: illustration */}
            <div className="flex justify-center">
              <TrackingIllustration />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrackingSection;
